import * as React from 'react'

import EmptyState from '../empty_state'
import { StudentPracticeInterface, } from '../interfaces'
import { formatDateToMMDDYY, ALL, TABLE_WIDTH_PROPS} from '../shared'
import { getRoundedTotalTimeSpentString, } from '../../../../helpers/studentReports'
import { DataTable, Tooltip, Spinner, } from '../../../../../Shared/index'
import GradeColor, { GREEN, ORANGE, RED, } from '../../../modules/grade_color'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'

interface BySkillPropsInterface {
  studentPractices: StudentPracticeInterface[];
  selectedStudent: { value: number|string, label: string, }
  allSkillNames?: string[];
  additionalRequestRunning: boolean;
}

const BySkill = ({ studentPractices, selectedStudent, allSkillNames, additionalRequestRunning, }: BySkillPropsInterface) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const allSelected = selectedStudent?.value === ALL
  const relevantStudentPractice = studentPractices?.find(studentPractice => studentPractice.user_id === selectedStudent?.value)

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 240, minWidth: 240 },
      { width: 240 },
      { width: 240 },
      { width: 240 },
      { width: 240 },
    ],
    containerRef,
    ...TABLE_WIDTH_PROPS,
  });

  const headers = [
    {
      attribute: 'name',
      name: 'Skill',
      renderCellsAsHeaders: true,
      rowSectionClassName: 'name border-right',
      width: columnWidths['240w-240min'],
    },
    {
      attribute: 'activityCount',
      name: 'Total Activities Completed',
      width: columnWidths['240w'],
      textAlign: 'left',
      headerClassName: 'eight-px-right-padding',
    },
    {
      attribute: 'timespentForDisplay',
      name: 'Total Time Spent',
      width: columnWidths['240w'],
    },
    {
      attribute: 'lastPracticed',
      name: 'Last Practiced',
      width: columnWidths['240w'],
    },
    {
      attribute: 'results',
      name: 'Results',
      rowSectionClassName: 'show-overflow',
      width: columnWidths['240w'],
    },
  ];

  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: !!(allSelected || relevantStudentPractice),
    dependencies: [Object.values(columnWidths).join('|')], // ensure resync when columnWidths change
  });

  if (!studentPractices || additionalRequestRunning) {
    return (
      <div className="diagnostic-insights-content practice-insights">
        <Spinner />
      </div>
    )
  }

  if (!studentPractices.filter(sp => sp.skills?.length).length || !(allSelected || relevantStudentPractice?.skills?.length)) {
    return (
      <EmptyState
        bodyText="There is no practice history available for the filters selected. However, there may be history for previous versions of the selected diagnostic. Try selecting another version from the diagnostic dropdown or modifying the other filters. Alternatively, consider assigning a diagnostic."
        headerText="No practice insights are available"
      />
    )
  }

  const renderResults = (percentages) => {
    let greenActivityCount = 0
    let orangeActivityCount = 0
    let redActivityCount = 0

    greenActivityCount = percentages.filter(percentage => GradeColor(percentage) === GREEN)?.length || 0
    orangeActivityCount = percentages.filter(percentage => GradeColor(percentage) === ORANGE)?.length || 0
    redActivityCount = percentages.filter(percentage => GradeColor(percentage) === RED)?.length || 0

    return (
      <div className="score-boxes">
        <Tooltip
          skipPositioning={true}
          tooltipText={`A green activity square indicates that the student frequently demonstrated the relevant writing skill and earned a percentage score of 83-100%. Total activities in this range: ${greenActivityCount}.`}
          tooltipTriggerText={<div aria-label={`Frequently demonstrated skill on ${greenActivityCount} practice ${greenActivityCount === 1 ? 'activity' : 'activities'}`} className="score-box green">{greenActivityCount}</div>}
        />
        <Tooltip
          skipPositioning={true}
          tooltipText={`A yellow activity square indicates that the student sometimes demonstrated the relevant writing skill and earned a percentage score of 32-82%. Total activities in this range: ${orangeActivityCount}.`}
          tooltipTriggerText={<div aria-label={`Sometimes demonstrated skill on ${orangeActivityCount} practice ${orangeActivityCount === 1 ? 'activity' : 'activities'}`} className="score-box orange">{orangeActivityCount}</div>}
        />
        <Tooltip
          skipPositioning={true}
          tooltipText={`A red activity square indicates that the student rarely demonstrated the relevant writing skill and earned a percentage score of 0-31%. Total activities in this range: ${redActivityCount}.`}
          tooltipTriggerText={<div aria-label={`Rarely demonstrated skill on ${redActivityCount} practice ${redActivityCount === 1 ? 'activity' : 'activities'}`} className="score-box red">{redActivityCount}</div>}
        />
      </div>
    )
  }

  const rows = () => {
    if (allSelected) {
      return allSkillNames.map(skillName => {
        const allRelevantSkillPractices = studentPractices.map(sp => sp.skills.find(s => s.skill_name === skillName)).filter(Boolean)

        const lastPracticedDatesToNumbers = allRelevantSkillPractices.map(sp => Number(new Date(sp?.last_practiced)))

        return {
          id: skillName,
          name: skillName,
          activityCount: allRelevantSkillPractices.reduce((sum, skillPractice) => sum + skillPractice.activity_count, 0),
          timespentForDisplay: getRoundedTotalTimeSpentString(allRelevantSkillPractices.map(skillPractice => skillPractice.timespent).flat()),
          lastPracticed: formatDateToMMDDYY(Math.max(...lastPracticedDatesToNumbers)),
          results: renderResults(allRelevantSkillPractices.map(sp => sp.percentages).flat())
        }
      })
    }

    return relevantStudentPractice.skills.map(skill => {
      const { skill_name, activity_count, timespent, last_practiced, percentages, } = skill

      return {
        id: skill_name,
        name: skill_name,
        activityCount: activity_count,
        timespentForDisplay: getRoundedTotalTimeSpentString(timespent),
        lastPracticed: formatDateToMMDDYY(last_practiced),
        results: renderResults(percentages)
      }
    })
  }

  return (
    <section className="diagnostic-insights-content practice-insights" ref={containerRef}>
      <div className="data-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          headers={headers}
          reportingFormat={true}
          rows={rows()}
          tableRef={tableRef}
        />
      </div>
    </section>
  )
}
export default BySkill
